import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { shareAPI } from '../api';
import { deserializeFromShare, logCanonicalChecksum, getCanonicalDecision } from '../utils/canonicalStateSerializer';
import { startLogin } from '../utils/authProvider';
import Header from '../components/Header';
import { 
  MapPin, DollarSign, Home, TrendingUp, Calendar, Ruler, Bed, Bath,
  CheckCircle2, AlertTriangle, XCircle, Loader2, Lock, ArrowRight, Target
} from 'lucide-react';

const SharedAnalysis = () => {
  const { analysisId } = useParams();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadAnalysis = async () => {
      try {
        const response = await shareAPI.getSharedAnalysis(analysisId);
        
        // CANONICAL DESERIALIZER: Use the shared deserializer to create canonical state
        // This logs the checksum automatically at SHARE_LOAD
        const canonicalState = deserializeFromShare(response.data);
        
        // Set state with the canonical deserialized object
        setAnalysis(canonicalState);
        
      } catch (err) {
        setError(err.response?.data?.detail || 'Analysis not found');
      } finally {
        setLoading(false);
      }
    };
    
    loadAnalysis();
  }, [analysisId]);

  // =====================================================
  // CANONICAL STATE: Direct consumption from persisted snapshot
  // The analysis object IS the canonical state - NO reconstruction
  // =====================================================
  const sharedDeal = useMemo(() => {
    if (!analysis) return null;

    return {
      // The spread is the point. What followed used to be a hand-copied list of ~44
      // fields, which meant every field the backend added had to be remembered here
      // too — and arv_confidence was not: it arrived in the payload, was skipped by
      // the copy, and the Confidence panel rendered blank on every shared link. The
      // header above this block already said "the analysis object IS the canonical
      // state - NO reconstruction"; the code just did not do it.
      //
      // Spread FIRST so the derived and normalised entries below still win.
      ...analysis,

      // Core identification
      type: analysis.type,
      analysis_id: analysis.analysis_id,
      
      // Property info
      address: analysis.address,
      city: analysis.city,
      state: analysis.state,
      zip_code: analysis.zip_code,
      property_type: analysis.property_type,
      beds: analysis.beds,
      baths: analysis.baths,
      sqft: analysis.sqft,
      year_built: analysis.year_built,
      
      // Valuation
      purchase_price: analysis.purchase_price,
      arv: analysis.arv,
      arv_mode: analysis.arv_mode,
      rehab_estimate: analysis.rehab_estimate,
      
      // Deal assessment
      deal_score: analysis.deal_score,
      deal_rating: analysis.deal_rating,
      deal_classification: analysis.deal_classification,
      deal_decision: analysis.deal_decision,
      deal_explanation: analysis.deal_explanation,
      
      // User criteria
      user_criteria: analysis.user_criteria,
      
      // Financing mode - from canonical state
      financing_mode: analysis.financing_mode,
      
      // Flip analysis - directly from canonical state
      net_profit: analysis.flip_analysis?.net_profit,
      loan_costs: analysis.flip_analysis?.loan_costs,
      profit_margin: analysis.flip_analysis?.profit_margin,
      mao: analysis.flip_analysis?.mao,
      // flip_analysis has no `roi` field, so this was always undefined and the shared
      // page printed "0.0%" to whoever the deal was sent to. ROI is profit over the cash
      // actually put in; derive it from the two fields the payload does send.
      roi: (Number(analysis.flip_analysis?.cash_invested) > 0)
        ? (Number(analysis.flip_analysis?.net_profit || 0)
           / Number(analysis.flip_analysis.cash_invested)) * 100
        : null,
      total_project_cost: analysis.flip_analysis?.total_project_cost,
      flip_cash_invested: analysis.flip_analysis?.cash_invested,
      flip_analysis: analysis.flip_analysis,
      
      // Rental analysis
      rental_analysis: analysis.rental_analysis,
      // Rental fields for direct access (with None-safety)
      monthly_cashflow: analysis.rental_analysis?.monthly_cashflow || 0,
      dscr: analysis.rental_analysis?.dscr || 0,
      cap_rate: analysis.rental_analysis?.cap_rate || 0,
      cash_invested: analysis.rental_analysis?.cash_invested || 0,
      // A cash-out REFINANCE has no purchase and no cash invested — cash is RECEIVED.
      // Without these the lender-facing share showed "Asking Price" and "Cash Invested $0"
      // for a deal that returns money to the owner.
      rental_purchase_type: analysis.rental_analysis?.rental_purchase_type,
      refi_cash_out: analysis.rental_analysis?.refi_cash_out || 0,
      
      // Strategy
      strategy: analysis.strategy,
      
      // Comparables
      comparable_count: analysis.comparable_count,
      arv_details: analysis.arv_details,
    };
  }, [analysis]);

  // Log checksum when sharedDeal is ready for rendering
  useEffect(() => {
    if (sharedDeal) {
      logCanonicalChecksum('SHARE_RENDER', {
        financing_mode: sharedDeal.financing_mode,
        flip_analysis: sharedDeal.flip_analysis,
        deal_classification: sharedDeal.deal_classification,
        arv: sharedDeal.arv,
        comparable_count: sharedDeal.comparable_count,
        arv_details: sharedDeal.arv_details,
      });
    }
  }, [sharedDeal]);

  // GAP #2: honest-blocked ARV — the shared (lender-facing) view must not present a
  // confident ARV-derived number (value, equity); show the same "add renovated comps" state.
  const sharedArvBlocked = sharedDeal?.verdict_reliable === false
    || ['NO_RENOVATED_COMPS', 'UNABLE_TO_COMPUTE'].includes(sharedDeal?.arv_details?.arv_status);

  // P0 FIX: Use getCanonicalDecision() for SINGLE SOURCE OF TRUTH
  // REMOVED: Local decision mapping that could diverge from Results page
  const getRecommendation = () => {
    if (!sharedDeal) return null;
    
    // P0 FIX: Use canonical decision helper - matches Results, PDF, ShareModal
    const canonicalDecision = getCanonicalDecision(sharedDeal, 'en');
    const financingMode = sharedDeal.financing_mode;
    
    // Determine financing text STRICTLY from financingMode
    // A refinance is neither a cash purchase nor a financed purchase — it is not a
    // purchase. Labelling a DSCR cash-out refi "(Cash purchase)" misdescribes the deal to
    // whoever the link was sent to.
    const financingText = sharedDeal.rental_purchase_type === 'refinancing'
      ? '(Cash-out refinance)'
      : financingMode === 'financed'
        ? '(Financed)'
        : financingMode === 'cash'
          ? '(Cash purchase)'
          : '';
    
    // Map canonical decision to recommendation display
    if (canonicalDecision.color === 'green') {
      return {
        label: canonicalDecision.label,
        color: 'green',
        icon: CheckCircle2,
        text: `This deal meets your investment criteria ${financingText}`
      };
    } else if (canonicalDecision.color === 'amber') {
      return {
        label: canonicalDecision.label,
        color: 'amber',
        icon: AlertTriangle,
        text: `This deal needs negotiation to meet criteria ${financingText}`
      };
    }
    // PASS
    return {
      label: canonicalDecision.label,
      color: 'red',
      icon: XCircle,
      text: `This deal does not meet your investment criteria ${financingText}`
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Analysis Not Found</h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/analyze')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Analyze Your Own Deal
            </button>
          </div>
        </div>
      </div>
    );
  }

  const recommendation = getRecommendation();
  const RecommendationIcon = recommendation?.icon || AlertTriangle;

  // Helper to format currency consistently (rounded to nearest dollar)
  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '$0';
    return '$' + Math.round(value).toLocaleString();
  };

  // Check if this is a valuation-only analysis
  const isValuationMode = sharedDeal?.strategy === 'valuation';
  const isRentalMode = sharedDeal?.strategy === 'rental';

  // For non-logged-in users, show a preview with signup prompt
  const showFullAnalysis = !!user;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Shared badge */}
        <div className="mb-6 flex items-center gap-2 text-sm text-slate-500">
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
            {isValuationMode ? 'Shared Valuation Report' : 'Shared Analysis'}
          </span>
          {isValuationMode && (
            <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full font-medium">
              📊 Property Value / Comps
            </span>
          )}
        </div>

        {/* Property Header */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-secondary to-blue-700 text-white p-6">
            <div className="flex items-start gap-3">
              <MapPin className="w-6 h-6 flex-shrink-0 mt-1" />
              <div>
                <h1 className="text-2xl font-bold">{sharedDeal.address}</h1>
                <p className="text-blue-100">
                  {sharedDeal.city}, {sharedDeal.state} {sharedDeal.zip_code}
                </p>
              </div>
            </div>
          </div>
          
          {/* Property Details */}
          <div className="p-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-800">{formatCurrency(sharedDeal.purchase_price)}</div>
                <div className="text-xs text-slate-500">
                  {/* nosemgrep: purchase-price-must-be-refinance-aware -- refinancing ternary on this line */}
                  {sharedDeal.rental_purchase_type === 'refinancing' ? 'Appraised Value' : 'Asking Price'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                {/* The VALUE itself is always shown when one exists — see Results.js. What
                    stays blocked below is everything DERIVED from it (equity, profit, ROI,
                    MAO): a lender must not read a precise downstream figure off an ARV we
                    already said needs better comps. */}
                {sharedArvBlocked ? (
                  <>
                    {sharedDeal.arv > 0 && (
                      <div className="text-lg font-bold text-amber-800">{formatCurrency(sharedDeal.arv)}</div>
                    )}
                    <div className="text-xs font-semibold text-amber-700">Add your comps for a stronger ARV</div>
                  </>
                ) : (
                  <div className="text-lg font-bold text-blue-600">{formatCurrency(sharedDeal.arv)}</div>
                )}
                <div className="text-xs text-slate-500">Estimated ARV</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Ruler className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-800">{sharedDeal.sqft?.toLocaleString()}</div>
                <div className="text-xs text-slate-500">Sq Ft</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Calendar className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-800">{sharedDeal.year_built}</div>
                <div className="text-xs text-slate-500">Year Built</div>
              </div>
            </div>
          </div>
          
          <div className="px-6 pb-6 flex gap-4 text-sm text-slate-600">
            <span className="flex items-center gap-1">
              <Bed className="w-4 h-4" /> {sharedDeal.beds} bed
            </span>
            <span className="flex items-center gap-1">
              <Bath className="w-4 h-4" /> {sharedDeal.baths} bath
            </span>
            <span className="flex items-center gap-1">
              <Home className="w-4 h-4" /> {sharedDeal.property_type?.replace('_', ' ')}
            </span>
          </div>
        </div>

        {/* Investment Recommendation - Hidden in valuation mode */}
        {!isValuationMode && recommendation && (
          <div className={`mb-6 p-6 rounded-2xl border-2 ${
            recommendation.color === 'green' ? 'bg-green-50 border-green-300' :
            recommendation.color === 'amber' ? 'bg-amber-50 border-amber-300' :
            'bg-red-50 border-red-300'
          }`}>
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${
                recommendation.color === 'green' ? 'bg-green-500' :
                recommendation.color === 'amber' ? 'bg-amber-500' :
                'bg-red-500'
              }`}>
                <RecommendationIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className={`text-2xl font-bold ${
                  recommendation.color === 'green' ? 'text-green-700' :
                  recommendation.color === 'amber' ? 'text-amber-700' :
                  'text-red-700'
                }`}>
                  {recommendation.label}
                </div>
                <p className="text-slate-600">{recommendation.text}</p>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Summary - Available to everyone */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            {isValuationMode ? 'Property Valuation Summary' : 'Analysis Summary'}
          </h2>
          <div className={`grid ${isValuationMode ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'} gap-4`}>
            <div className="p-4 bg-slate-50 rounded-xl text-center">
              <div className="text-sm text-slate-500 mb-1">{isValuationMode ? 'Estimated Value' : 'ARV'}</div>
              {sharedArvBlocked ? (
                <>
                  {sharedDeal.arv > 0 && (
                    <div className="text-xl font-bold text-amber-800" data-testid="shared-arv-amount">
                      {formatCurrency(sharedDeal.arv)}
                    </div>
                  )}
                  <div className="text-xs font-semibold text-amber-700">Add your comps for a stronger ARV</div>
                  {sharedDeal?.arv_details?.arv_warning && (
                    <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                      {sharedDeal.arv_details.arv_warning}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-xl font-bold text-slate-800" data-testid="shared-arv-amount">
                  {formatCurrency(sharedDeal.arv)}
                </div>
              )}
            </div>
            {/* Equity Spread for valuation mode */}
            {isValuationMode && (
              <div className="p-4 bg-slate-50 rounded-xl text-center">
                <div className="text-sm text-slate-500 mb-1">Potential Equity</div>
                {sharedArvBlocked ? (
                  <div className="text-sm font-bold text-amber-700">Needs reliable ARV</div>
                ) : (
                  <div className={`text-xl font-bold ${(sharedDeal.arv - sharedDeal.purchase_price) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(sharedDeal.arv - sharedDeal.purchase_price)}
                  </div>
                )}
              </div>
            )}
            <div className="p-4 bg-slate-50 rounded-xl text-center">
              <div className="text-sm text-slate-500 mb-1">Confidence</div>
              <div className="text-xl font-bold text-slate-800 capitalize">{sharedDeal.arv_confidence}</div>
            </div>
            {/* Investment metrics - hide in valuation mode */}
            {/* FLIP metrics - only for flip strategy */}
            {!isValuationMode && !isRentalMode && (
              <>
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <div className="text-sm text-slate-500 mb-1">Rehab Est.</div>
                  <div className="text-xl font-bold text-slate-800">{formatCurrency(sharedDeal.rehab_estimate)}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <div className="text-sm text-slate-500 mb-1">Net Profit</div>
                  {/* Blocked ARV: profit is ARV-derived. A lender opening this link must
                      not read a precise figure we already said we cannot support. */}
                  {sharedArvBlocked ? (
                    <div className="text-sm font-bold text-amber-700" data-testid="shared-profit-blocked">Add renovated comps</div>
                  ) : (
                    <div className={`text-xl font-bold ${sharedDeal.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(sharedDeal.net_profit)}
                    </div>
                  )}
                </div>
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <div className="text-sm text-slate-500 mb-1">Loan Costs</div>
                  <div className="text-xl font-bold text-slate-800">
                    {formatCurrency(sharedDeal.loan_costs || 0)}
                    {(sharedDeal.loan_costs || 0) === 0 && (
                      <span className="text-xs ml-1 text-emerald-600">(Cash)</span>
                    )}
                  </div>
                </div>
              </>
            )}
            {/* RENTAL metrics - only for rental strategy */}
            {isRentalMode && (
              <>
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <div className="text-sm text-slate-500 mb-1">Monthly Cash Flow</div>
                  <div className={`text-xl font-bold ${sharedDeal.monthly_cashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(sharedDeal.monthly_cashflow)}
                  </div>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <div className="text-sm text-slate-500 mb-1">Cap Rate</div>
                  <div className="text-xl font-bold text-slate-800">{sharedDeal.cap_rate.toFixed(1)}%</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <div className="text-sm text-slate-500 mb-1">DSCR</div>
                  <div className="text-xl font-bold text-blue-600">{sharedDeal.dscr.toFixed(2)}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <div className="text-sm text-slate-500 mb-1">
                    {sharedDeal.rental_purchase_type === 'refinancing' ? 'Cash Out Received' : 'Cash Invested'}
                  </div>
                  <div className="text-xl font-bold text-slate-800">
                    {formatCurrency(sharedDeal.rental_purchase_type === 'refinancing'
                      ? sharedDeal.refi_cash_out : sharedDeal.cash_invested)}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Full Details - Only for logged-in users */}
        {showFullAnalysis ? (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">
              {isValuationMode ? 'Comparable Sales Details' : 'Detailed Analysis'}
            </h2>
            
            {/* Fix & Flip Analysis - Hidden in valuation AND rental mode */}
            {!isValuationMode && !isRentalMode && (
            <div className="mb-6">
              <h3 className="text-md font-medium text-slate-700 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                Fix & Flip Analysis
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500">Total Project Cost</div>
                  <div className="font-semibold">{formatCurrency(sharedDeal.total_project_cost)}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500">Profit Margin</div>
                  <div className="font-semibold" data-testid="shared-margin">
                    {sharedArvBlocked ? '—' : `${(sharedDeal.profit_margin || 0).toFixed(1)}%`}
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500">ROI</div>
                  <div className="font-semibold" data-testid="shared-roi">
                    {/* roi is null when there is no cash basis to divide by. Show "—",
                        not "0.0%" — a real zero return and an unknown one are different
                        claims to make to whoever this deal was shared with. */}
                    {(sharedArvBlocked || !Number.isFinite(sharedDeal.roi))
                      ? '—' : `${sharedDeal.roi.toFixed(1)}%`}
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500">Target Offer Price</div>
                  <div className="font-semibold" data-testid="shared-target-offer">
                    {(sharedArvBlocked || !(sharedDeal.mao > 1)) ? '—' : formatCurrency(sharedDeal.mao)}
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500">Cash Invested</div>
                  <div className="font-semibold">{formatCurrency(sharedDeal.flip_analysis?.cash_invested)}</div>
                </div>
              </div>
            </div>
            )}
            
            {/* Rental Analysis - Only for rental strategy */}
            {isRentalMode && sharedDeal.rental_analysis && (
              <div>
                <h3 className="text-md font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Home className="w-4 h-4 text-green-600" />
                  Rental Analysis
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">Monthly Cash Flow</div>
                    <div className="font-semibold">{formatCurrency(sharedDeal.monthly_cashflow)}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">Cap Rate</div>
                    <div className="font-semibold">{sharedDeal.cap_rate.toFixed(1)}%</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">DSCR</div>
                    <div className="font-semibold">{sharedDeal.dscr.toFixed(2)}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">Cash-on-Cash</div>
                    <div className="font-semibold">{(sharedDeal.rental_analysis?.cash_on_cash_return || 0).toFixed(1)}%</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500">Cash Invested</div>
                    <div className="font-semibold">{formatCurrency(sharedDeal.cash_invested)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Signup prompt for non-logged-in users */
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-lg p-8 text-center text-white">
            <Lock className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <h2 className="text-2xl font-bold mb-2">Get the Full Analysis</h2>
            <p className="text-blue-100 mb-6 max-w-md mx-auto">
              Create a free CHEKSTE account to see the complete breakdown including 
              detailed flip analysis, rental projections, rehab estimates, and comparable sales.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => {
                  // Provider (emergent | google) resolved at runtime from /api/auth/config.
                  startLogin('/analyze');
                }}
                className="px-6 py-3 bg-white text-blue-700 rounded-lg font-medium hover:bg-slate-100 transition-colors flex items-center justify-center gap-2"
              >
                Create Free Account
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/analyze')}
                className="px-6 py-3 bg-blue-500/30 text-white rounded-lg font-medium hover:bg-blue-500/40 transition-colors"
              >
                Analyze Your Own Deal
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SharedAnalysis;
